import Reveal from '../Components/Reveal';
import '../styles/Home.css';
import utpLogo from '../Assets/UTP_logo.png';
import cradleLogo from '../Assets/cradle_logo.png';
import petronasLogo from '../Assets/petronas_logo.png';
import mdecLogo from '../Assets/mdec_logo.png';
import heroBg from '../Assets/oilngas_bg.avif';
import oilRigImg from '../Assets/oil_rig.avif';
import energyImg from '../Assets/energy.avif';
import aiImg from '../Assets/ai.jpg';
import analyticsImg from '../Assets/analytics.jpg';

/* ── SVG ICONS ────────────────────────────────────────────── */
const IconAI = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
    <path d="M5 3 4 6l-3 1 3 1 1 3 1-3 3-1-3-1Z" />
    <path d="M19 15l-.867 2.6a2 2 0 0 1-1.274 1.274L14 20l2.859.953a2 2 0 0 1 1.274 1.274L19 25l.953-2.859a2 2 0 0 1 1.274-1.274L24 20l-2.773-.924a2 2 0 0 1-1.274-1.274Z" />
  </svg>
);

const IconCloud = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2v8" />
    <path d="m4.93 10.93 1.41 1.41" />
    <path d="M2 18h2" />
    <path d="M20 18h2" />
    <path d="m19.07 10.93-1.41 1.41" />
    <path d="M22 22H2" />
    <path d="m8 22 4-10 4 10" />
    <path d="M17 18a5 5 0 0 0-10 0" />
  </svg>
);

const ArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

/* ── HOME PAGE ────────────────────────────────────────────── */
const Home = () => {
  return (
    <>
      {/* ── SECTION 1: HERO ─────────────────────────────────── */}
      <section className="ew-hero">
        <div className="ew-hero__inner">
          <Reveal>
            <div className="ew-hero__card">
              <img
                src={heroBg}
                alt="Energy operations"
                className="ew-hero__img"
              />
              <div className="ew-hero__overlay">
                <span className="ew-hero__eyebrow">
                  Intelligence for Energy &amp; Oil &amp; Gas
                </span>
                <h1 className="ew-hero__heading">
                  Transforming{' '}
                  <span className="ew-hero__heading-muted">Data Chaos</span>{' '}
                  into Strategic Intelligence
                </h1>
                <p className="ew-hero__sub">
                  We deliver the digital architecture, engineering precision,
                  and specialized training required to drive smarter, leaner,
                  and more agile energy operations.
                </p>
                <div className="ew-hero__btns">
                  <a href="#solutions" className="ew-hero__btn-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector('#solutions')?.scrollIntoView({ behavior: 'smooth' });
                    }}>
                    Explore Solutions
                  </a>
                  <a href="#products" className="ew-hero__btn-secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector('#products')?.scrollIntoView({ behavior: 'smooth' });
                    }}>
                    View Products
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── SECTION 2: ABOUT / PROBLEM ──────────────────────── */}
      <section className="ew-about" id="about">
        <div className="ew-about__inner">
          {/* Left column */}
          <Reveal delay={0}>
            <div>
              <p className="ew-about__eyebrow">The Friction Point</p>
              <h2 className="ew-about__heading">
                <span className="ew-about__stat">80%</span> of energy data
                never reaches decision-makers
              </h2>
              <p className="ew-about__body">
                Energy and Oil &amp; Gas companies generate enormous volumes of
                operational data every day — but fragmented systems, outdated
                workflows, and siloed information pipelines mean critical
                intelligence is lost before it ever drives action.
              </p>
              <p className="ew-about__body">
                Teczo EnergyWorks bridges this gap with purpose-built digital
                solutions that capture, structure, and surface the intelligence
                your operations depend on.
              </p>
              <div className="ew-about__divider">
                <div className="ew-about__divider-line" />
                <span className="ew-about__divider-text">
                  Precision Engineering Meets Digital Intelligence
                </span>
              </div>
            </div>
          </Reveal>

          {/* Right column: staggered image/stat grid */}
          <Reveal delay={150}>
            <div className="ew-about__grid">
              {/* Column A — offset down */}
              <div className="ew-about__col-a">
                <div className="ew-about__stat-card">
                  <p className="ew-about__stat-num">
                    <span style={{ color: '#d97706' }}>40%</span>
                  </p>
                  <p className="ew-about__stat-label">Avg Cost Reduction</p>
                </div>
                <img
                  src={oilRigImg}
                  alt="Pipeline infrastructure"
                  className="ew-about__img"
                />
              </div>

              {/* Column B — flush top */}
              <div className="ew-about__col-b">
                <img
                  src={energyImg}
                  alt="Energy analytics dashboard"
                  className="ew-about__img"
                />
                <div className="ew-about__dark-card">
                  <h4>Real-time Intelligence</h4>
                  <p>
                    Live dashboards and AI-driven alerts keep your teams ahead
                    of operational risks and market shifts.
                  </p>
                </div>
                <div className="ew-about__stat-card">
                  <p className="ew-about__stat-num">
                    <span style={{ color: '#d97706' }}>3x</span>
                  </p>
                  <p className="ew-about__stat-label">Faster Decision Cycles</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── SECTION 3: SOLUTIONS ────────────────────────────── */}
      <section className="ew-solutions" id="solutions">
        <div className="ew-solutions__card">
          {/* Decorative glow */}
          <div className="ew-solutions__glow" aria-hidden="true" />

          {/* Header */}
          <Reveal delay={0}>
            <div className="ew-solutions__header">
              <p className="ew-solutions__eyebrow">Our Capabilities</p>
              <h2 className="ew-solutions__heading">
                Built for the Energy Industry
              </h2>
            </div>
          </Reveal>

          {/* 2 capability cards */}
          <div className="ew-solutions__grid">
            {/* Card 1: AI & ML */}
            <Reveal delay={100}>
              <div className="ew-solutions__cap-card">
                <div className="ew-solutions__icon">
                  <IconAI />
                </div>
                <h3 className="ew-solutions__cap-heading">
                  AI &amp; Machine Learning
                </h3>
                <p className="ew-solutions__cap-body">
                  Purpose-built AI models trained on industry-specific datasets
                  to detect anomalies, predict failures, and surface
                  optimization opportunities before they become costly problems.
                </p>
                <ul className="ew-solutions__cap-list">
                  <li>
                    <span className="ew-solutions__arrow"><ArrowRight /></span>
                    Predictive Maintenance &amp; Anomaly Detection
                  </li>
                  <li>
                    <span className="ew-solutions__arrow"><ArrowRight /></span>
                    Natural Language Processing for Technical Documents
                  </li>
                  <li>
                    <span className="ew-solutions__arrow"><ArrowRight /></span>
                    Production Optimization Models
                  </li>
                  <li>
                    <span className="ew-solutions__arrow"><ArrowRight /></span>
                    Automated Reporting &amp; Insight Generation
                  </li>
                </ul>
              </div>
            </Reveal>

            {/* Card 2: Cloud & Architecture */}
            <Reveal delay={200}>
              <div className="ew-solutions__cap-card">
                <div className="ew-solutions__icon">
                  <IconCloud />
                </div>
                <h3 className="ew-solutions__cap-heading">
                  Cloud &amp; Architecture
                </h3>
                <p className="ew-solutions__cap-body">
                  Scalable cloud infrastructure designed for the demanding
                  requirements of energy operations — from offshore data
                  pipelines to enterprise-wide digital transformation programs.
                </p>
                <ul className="ew-solutions__cap-list">
                  <li>
                    <span className="ew-solutions__arrow"><ArrowRight /></span>
                    Industrial IoT Data Pipelines
                  </li>
                  <li>
                    <span className="ew-solutions__arrow"><ArrowRight /></span>
                    Cloud Migration &amp; Hybrid Architecture
                  </li>
                  <li>
                    <span className="ew-solutions__arrow"><ArrowRight /></span>
                    Real-time SCADA &amp; Sensor Integration
                  </li>
                  <li>
                    <span className="ew-solutions__arrow"><ArrowRight /></span>
                    Security-first Design for OT/IT Convergence
                  </li>
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── SECTION 4: PRODUCTS ─────────────────────────────── */}
      <section className="ew-products" id="products">
        <div className="ew-products__inner">
          {/* Header row */}
          <Reveal delay={0}>
            <div className="ew-products__header">
              <div>
                <p className="ew-products__eyebrow">Our Products</p>
                <h2 className="ew-products__heading">
                  Software Built for Energy
                </h2>
              </div>
            </div>
          </Reveal>

          {/* 2 product cards */}
          <div className="ew-products__grid">
            {/* DocuNest.AI */}
            <Reveal delay={100}>
              <div className="ew-products__item">
                <img
                  src={aiImg}
                  alt="DocuNest.AI — document intelligence platform"
                  className="ew-products__item-img"
                />
                <div className="ew-products__item-overlay ew-products__item-overlay--dark">
                  <p className="ew-products__item-eyebrow">Document Intelligence</p>
                  <h3 className="ew-products__item-title">DocuNest.AI</h3>
                  <p className="ew-products__item-desc">
                    AI-powered document management that extracts, classifies,
                    and surfaces knowledge from your technical library — from
                    well reports to compliance documents.
                  </p>
                </div>
              </div>
            </Reveal>

            {/* EnergyStudio */}
            <Reveal delay={200}>
              <div className="ew-products__item">
                <img
                  src={analyticsImg}
                  alt="EnergyStudio — operations analytics platform"
                  className="ew-products__item-img"
                />
                <div className="ew-products__item-overlay ew-products__item-overlay--orange">
                  <p className="ew-products__item-eyebrow">Operations Analytics</p>
                  <h3 className="ew-products__item-title">EnergyStudio</h3>
                  <p className="ew-products__item-desc">
                    A unified analytics workspace for energy engineers —
                    connect your data sources, build custom dashboards, and
                    generate actionable operational insights.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── SECTION 5: IMPACT / ACHIEVEMENTS ────────────────── */}
      <section className="ew-impact" id="achievements">
        <div className="ew-impact__inner">
          {/* Header */}
          <Reveal delay={0}>
            <div className="ew-impact__header">
              <p className="ew-impact__eyebrow">Our Impact</p>
              <h2 className="ew-impact__heading">Achievements That Matter</h2>
              <p className="ew-impact__sub">
                Recognized by industry leaders and government agencies for
                delivering measurable results in Malaysia's energy and
                technology ecosystem.
              </p>
            </div>
          </Reveal>

          {/* 4 achievement cards */}
          <div className="ew-impact__grid">
            {/* Card 1: UTP Preferred Vendor */}
            <Reveal delay={100}>
              <div className="ew-impact__card">
                <img src={utpLogo} alt="UTP" className="ew-impact__card-logo" />
                <p className="ew-impact__card-label">Recognition</p>
                <h3 className="ew-impact__card-title">
                  UTP Preferred Vendor Award
                </h3>
                <p className="ew-impact__card-body">
                  Recognized as a preferred technology partner by Universiti
                  Teknologi PETRONAS for delivering high-impact digital
                  solutions to the energy research community.
                </p>
              </div>
            </Reveal>

            {/* Card 2: Cradle CIP SPARK */}
            <Reveal delay={150}>
              <div className="ew-impact__card">
                <img src={cradleLogo} alt="Cradle" className="ew-impact__card-logo ew-impact__card-logo--cradle" />
                <p className="ew-impact__card-label">Grant</p>
                <h3 className="ew-impact__card-title">
                  Cradle CIP SPARK Grant Recipient
                </h3>
                <p className="ew-impact__card-body">
                  Secured competitive funding from Cradle Fund under the CIP
                  SPARK program to accelerate AI product development for the
                  oil and gas sector.
                </p>
              </div>
            </Reveal>

            {/* Card 3: PETRONAS Upstream */}
            <Reveal delay={200}>
              <div className="ew-impact__card">
                <img src={petronasLogo} alt="PETRONAS" className="ew-impact__card-logo" />
                <p className="ew-impact__card-label">Client Success</p>
                <p className="ew-impact__card-stat">~80%</p>
                <h3 className="ew-impact__card-title">
                  PETRONAS Upstream Efficiency Gain
                </h3>
                <p className="ew-impact__card-body">
                  Delivered a document intelligence solution that reduced
                  manual data extraction time by approximately 80% for
                  PETRONAS upstream operations.
                </p>
              </div>
            </Reveal>

            {/* Card 4: MDEC Content Grant */}
            <Reveal delay={250}>
              <div className="ew-impact__card">
                <img src={mdecLogo} alt="MDEC" className="ew-impact__card-logo" />
                <p className="ew-impact__card-label">Grant</p>
                <h3 className="ew-impact__card-title">
                  MDEC Content Development Grant
                </h3>
                <p className="ew-impact__card-body">
                  Awarded the Malaysia Digital Economy Corporation content
                  grant to develop specialized digital learning materials for
                  the energy engineering workforce.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── SECTION 6: CTA ──────────────────────────────────── */}
      <section className="ew-cta">
        <Reveal delay={0}>
          <div className="ew-cta__card">
            {/* Decorative glow */}
            <div className="ew-cta__glow" aria-hidden="true" />

            <div className="ew-cta__inner">
              <h2 className="ew-cta__heading">
                Ready to Turn Data Chaos<br />into Intelligence?
              </h2>
              <p className="ew-cta__sub">
                Let's build the digital foundation your energy operations need
                to stay ahead — faster decisions, leaner processes, and
                smarter outcomes.
              </p>
              <button
                className="ew-cta__btn"
                onClick={() =>
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })
                }
              >
                Let's Collaborate
              </button>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
};

export default Home;
